import React from 'react'
import { Card, Col, Row } from 'react-bootstrap'
import { useLocation } from 'react-router'
import { trans } from 'sharedKernel/i18n'
import { Loading } from 'components/loading'
import { NoContent } from 'components/NoContent'
import {
  AttachmentsArea,
  AttachmentsItem,
  AttachmentsTitle,
  CoverImage,
  DescriptionText,
  DescriptionTitle
} from 'sharedKernel/styledComponents/visualizationComponents/styles'
import { getProvider } from 'sharedKernel/api'
import { VisualizationEventScreenProps } from './contracts'
import { BreakWordParagraph, ScreenContainer } from './styles'
import DOMPurify from "dompurify"

export const EventVisualization = ({
  isFetching,
  data,
  isFileFetching,
  fileData
}: VisualizationEventScreenProps): JSX.Element => {
  const filesEvent = {
    eventFiles: fileData?.filter((file) => file.type === 'supportFiles'),
    homePageSlider: fileData?.find((file) => file.type === 'homePageSlider'),
    coverImage: fileData?.find((file) => file.type === 'coverImage')
  }
  const today = new Date()

  const labels = {
    notFound: trans('Não encontrado'),
    description: trans('Descrição'),
    attachments: trans('Anexos'),
    supportFilesNotFound: trans('Nenhum arquivo de apoio foi incluído neste evento'),
    closed: trans('Encerrado')
  }

  if (isFetching ?? false) {
    return (
      <Loading />
    )
  }
  if (data === undefined) {
    return <NoContent text={labels.notFound} />
  }

  const eventId = data.id

  const getIdeaImage = (url?: string): string => {
    const fallBackIdeaImage = require('../../../../assets/images/Imagem_do_evento.png')

    return url != null && url.length > 0 ? url : fallBackIdeaImage
  }

  const isAvailable = (): string => {
    if (!data.data.isLaunched) {
      return 'Pendente'
    }
    const endDateString = data.data.endDate?.split('T')[0]
    const endTimeString = data.data.endTime
    let endDateTime: Date
    if (endDateString !== null && endTimeString !== null) {
      endDateTime = new Date(`${endDateString}T${endTimeString}`)
    } else {
      endDateTime = new Date(endDateString)
    }
    const isEnded = endDateTime < today

    if (isEnded) {
      return 'Encerrado'
    }

    return 'Disponível'
  }

  const formatDateString = (dataInicio: Date | null = null, dataFim: Date | null = null): string => {
    if (dataInicio == null && dataFim == null) {
      return ''
    }

    const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']

    if (dataInicio && !dataFim) {
      const dataInicioFormatada = `${dataInicio.getDate()} de ${meses[dataInicio.getMonth()]} ${dataInicio.getFullYear()}`
      return dataInicioFormatada
    }

    if (dataInicio && dataFim) {
      const dataInicioFormatada = `${dataInicio.getDate()} de ${meses[dataInicio.getMonth()]} ${dataInicio.getFullYear()}`
      const dataFimFormatada = `${dataFim.getDate()} de ${meses[dataFim.getMonth()]} ${dataFim.getFullYear()}`

      return dataInicioFormatada !== dataFimFormatada ? `${dataInicioFormatada} - ${dataFimFormatada}` : dataInicioFormatada
    }

    return ''
  }

  const formatAddress = (): string => {
    if (data.data.isInPerson === true) {
      if (data.data.address.id !== null && data.data.address.addressLineOne !== null) {
        return `${data.data.address.addressLineOne}, ${data.data.address.addressNumber} - ${data.data.address.addressLineTwo}, ${data.data.address.addressNeighborhood}, ${data.data.address.addressCity} - ${data.data.address.addressState}, ${data.data.address.addressZipCode}`
      }
    } else {
      return 'Online'
    }
    return ''
  }

  const checkUrl = (answer: string): boolean => {
    try {
      const url = new URL(answer)
      return true
    } catch (err) {
      return false
    }
  }

  return (
    <ScreenContainer>
      <Row>
        <Col sm={10} xxl={10}>
          <h2><b>{data.data.name}</b></h2>
        </Col>
      </Row>
      <CoverImage backgroundImg={getIdeaImage(filesEvent?.homePageSlider?.path)} />
      <Row>
        <Col sm={5} xxl={3}>
          <Card>
            <Card.Body>
              <Row>
                <Col sm={12} className="gy-3">
                  <h5><b>Informações Gerais</b></h5>
                </Col>
                <Col sm={12} className="gy-3">
                  <BreakWordParagraph><b>{data.data.isPaid === true ? 'Evento Pago' : 'Evento Gratuito'}</b></BreakWordParagraph>
                </Col>
                <Col sm={12} className="gy-3">
                  <BreakWordParagraph><b>Status: </b>{isAvailable()}</BreakWordParagraph>
                </Col>
                <Col sm={12} className="gy-3">
                  <BreakWordParagraph><b>Data: </b>{formatDateString(data.data.startDate !== null && data.data.startDate !== '' ? new Date(data.data.startDate) : null, data.data.endDate !== null && data.data.endDate !== '' ? new Date(data.data.endDate) : null)}</BreakWordParagraph>
                </Col>
                <Col sm={12} className="gy-3">
                  <BreakWordParagraph><b>Localização: </b>{formatAddress()}</BreakWordParagraph>
                </Col>
                {data.data.website !== '' && (
                  <Col sm={12} className="gy-3">
                    <BreakWordParagraph><b>Site oficial do evento: </b>
                      {checkUrl(data.data.website) ? (
                        <a
                          href={data.data.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {data.data.website}
                        </a>
                      ) : (
                        data.data.website
                      )}
                    </BreakWordParagraph>
                  </Col>
                )}
                {filesEvent.eventFiles !== null && filesEvent.eventFiles !== undefined && filesEvent.eventFiles.length > 0 ? (
                  <Col sm={12} className="gy-4">
                    <AttachmentsArea>
                      <AttachmentsTitle>{labels.attachments}:</AttachmentsTitle>
                      {!isFileFetching ? (
                        filesEvent.eventFiles.map((file, index) => (
                          <AttachmentsItem key={`attachments-item-${index}`}>
                            <a key={`item-${index}`} href={file.path} target="_blank" download={file.name}>
                              {file.name}
                            </a>
                          </AttachmentsItem>
                        ))
                      ) : (
                        <AttachmentsItem>{labels.supportFilesNotFound}</AttachmentsItem>
                      )}
                    </AttachmentsArea>
                  </Col>
                ) : null}
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col sm={7} xxl={9}>
          <Row>
            <Col sm={12} className="gy-4">
              <DescriptionTitle>{labels.description}</DescriptionTitle>
              <hr />
              <DescriptionText>
                <div
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.data.description) }}
                />
                <br />
                <div style={{ marginTop: '2rem' }}>
                  <div
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(data.data.ticketDescription) }}
                  />
                </div>
              </DescriptionText>
            </Col>
          </Row>
        </Col>
      </Row>
    </ScreenContainer>
  )
}
